'use client';

import { useQuery } from '@tanstack/react-query';
import {
  auditKeys,
  fetchCsamAuditQueryResult,
  startCsamAuditQuery,
  type CsamAuditDimension,
  type CsamAuditQueryResult,
} from '@/shared/cors-audit-api';
import { useProvenanceSession } from './useProvenanceSession';

/** How long to keep polling a started query before giving up. */
const MAX_POLL_MS = 30_000;
/** Delay between poll attempts. */
const POLL_INTERVAL_MS = 2_000;
const DEFAULT_PAGE_SIZE = 100;

export interface CsamAuditQueryParams {
  dimension: CsamAuditDimension;
  /** Required for file/user/case; ignored for the tenant-wide dimension. */
  value?: string | null;
  /** Inclusive start date, ``YYYY-MM-DD`` (UTC). */
  from?: string | null;
  /** Inclusive end date, ``YYYY-MM-DD`` (UTC). */
  to?: string | null;
  pageSize?: number;
}

/** Resolve after ``ms``, or reject early if the abort signal fires. */
function delay(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException('Aborted', 'AbortError'));
      return;
    }
    const timer = setTimeout(() => {
      signal?.removeEventListener('abort', onAbort);
      resolve();
    }, ms);
    const onAbort = () => {
      clearTimeout(timer);
      reject(new DOMException('Aborted', 'AbortError'));
    };
    signal?.addEventListener('abort', onAbort, { once: true });
  });
}

/**
 * Start + poll a CSAM audit-log query. The whole start→poll lifecycle runs
 * inside a single TanStack query so the view gets one ``isFetching`` spinner
 * for the (up to ~30 s) wait and one ``data`` payload of rows at the end.
 *
 * The query is disabled until the caller flips ``opts.enabled`` (e.g. on a
 * "Run" click), so filter edits don't fire Athena queries on every keystroke.
 * The tenant and date bounds are enforced server-side; the client only chooses
 * the dimension/value and date window.
 */
export function useCsamAuditQuery(
  params: CsamAuditQueryParams,
  opts: { enabled?: boolean } = {},
) {
  const { cellId, accessToken, enabled: sessionEnabled } = useProvenanceSession();
  const { dimension, value = null, from = null, to = null, pageSize = DEFAULT_PAGE_SIZE } = params;

  // file/user/case require a value; tenant does not.
  const hasValue = dimension === 'tenant' || Boolean(value && value.trim());
  const enabled = sessionEnabled && Boolean(opts.enabled) && hasValue;

  return useQuery<CsamAuditQueryResult, Error>({
    queryKey: auditKeys.csamAuditQuery(cellId, {
      dimension,
      value,
      from,
      to,
      pageSize,
    }),
    queryFn: async ({ signal }) => {
      const { queryId } = await startCsamAuditQuery({
        cellId: cellId as string,
        accessToken: accessToken as string,
        dimension,
        value,
        from,
        to,
        pageSize,
        signal,
      });

      const deadline = Date.now() + MAX_POLL_MS;
      // Poll until the query leaves RUNNING or we hit the deadline.
      for (;;) {
        const result = await fetchCsamAuditQueryResult({
          cellId: cellId as string,
          accessToken: accessToken as string,
          queryId,
          signal,
        });
        if (result.status !== 'RUNNING') {
          return result;
        }
        if (Date.now() >= deadline) {
          throw new Error('Audit query timed out after 30s; narrow the date range and retry.');
        }
        await delay(POLL_INTERVAL_MS, signal);
      }
    },
    enabled,
    retry: false,
    refetchOnWindowFocus: false,
    staleTime: 60 * 1000,
    gcTime: 5 * 60 * 1000,
  });
}
